import {
  ApplicationConfig,
  importProvidersFrom,
  provideZonelessChangeDetection,
  provideAppInitializer,
  inject,
} from '@angular/core';
import { COUNTRIES, type CountryCode } from '@handyhand/i18n';
import type { Routes } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { RegionStore } from '@services/logic/region-store';
import {
  buildCountryAppRoutes,
  buildFallbackRoutes,
  buildHeadCategoryRoute,
} from './routes/app.routes';
import {
  provideRouter,
  ROUTES,
  withViewTransitions,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { provideToastr } from 'ngx-toastr';
import { registerLocaleData } from '@angular/common';
import localeDa from '@angular/common/locales/da';
import localeNl from '@angular/common/locales/nl';
import { localizationInterceptor } from './interceptors/localization.interceptor';
import { redirectInterceptor } from './interceptors/redirect.interceptor';
import { ssrTimeoutInterceptor } from './interceptors/ssr-timeout.interceptor';
import {
  provideNgxWebstorage,
  withLocalStorage,
  withNgxWebstorageConfig,
} from 'ngx-webstorage';
import { TranslateModule } from '@ngx-translate/core';
import { routingConfiguration } from '@app/routes/seo.routes';
import {
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
} from '@angular/platform-browser';
import { envProviders } from './core/env.providers';

registerLocaleData(localeDa, 'da-DK');
registerLocaleData(localeNl, 'nl-NL');

export const appConfig: ApplicationConfig = {
  providers: [
    ...envProviders,
    provideZonelessChangeDetection(),
    provideAppInitializer(() => firstValueFrom(inject(RegionStore).primeFromServer())),
    provideNgxWebstorage(
      withNgxWebstorageConfig({ separator: ':', caseSensitive: true }),
      withLocalStorage(),
    ),
    provideRouter(
      [],
      withViewTransitions(),
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
      withRouterConfig(routingConfiguration),
    ),
    {
      provide: ROUTES,
      multi: true,
      useFactory: (): Routes => {
        const countries: CountryCode[] = COUNTRIES.map(
          (country) => country.code,
        );
        const staticRoutes: Routes = [];
        const headCategoryRoutes: Routes = [];
        for (const country of countries) {
          staticRoutes.push(...buildCountryAppRoutes(country));
          headCategoryRoutes.push(...buildHeadCategoryRoute(country));
        }
        return [
          ...staticRoutes,
          ...headCategoryRoutes,
          ...buildFallbackRoutes(),
        ];
      },
    },
    provideClientHydration(
      withHttpTransferCacheOptions({
        includePostRequests: true,
        // Availability probes are reduced to a boolean and transferred via
        // TransferState (see MiniVerticalService); caching the raw response
        // would embed hundreds of KB of unused entity data in the page HTML.
        filter: (req) => !req.urlWithParams.includes('availabilityCheck=1'),
      }),
      withEventReplay(),
    ),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        ssrTimeoutInterceptor,
        localizationInterceptor,
        redirectInterceptor,
      ]),
    ),
    importProvidersFrom(TranslateModule.forRoot({})),
    provideAnimations(),
    provideToastr(),
  ],
};
